// Офлайн-движок тестів: будує набір питань по режиму з локальної бази, перевіряє
// відповіді й рахує результат — дзеркало серверної логіки (apps/api/src/routes/attempts.ts),
// щоб проходження офлайн збігалося з онлайн. Синхронізацію робить offlineSync.ts.
import { EXAM_CONFIG, XP_REWARDS } from '@app/shared';
import type {
  StartAttemptResponse,
  SubmitAnswerResponse,
  FinishAttemptResponse,
  ExamMode,
  Question,
} from './api';
import {
  allQuestions,
  enqueueAttempt,
  getMeta,
  questionById,
  type OfflineQuestion,
  type OutboxAttempt,
} from './offlineDb';

const LOCAL_PREFIX = 'local-';
export function isLocalAttempt(id: string): boolean {
  return id.startsWith(LOCAL_PREFIX);
}

// Режими, які можна відтворити офлайн. MISTAKES/BOOKMARKS залежать від серверної
// історії користувача — їх офлайн не пускаємо (кидаємо помилку → UI просить інтернет).
const OFFLINE_MODES: ExamMode[] = ['CATEGORY', 'OFFICIAL_20', 'RANDOM_20', 'MARATHON', 'HARDEST_100'];
export function isOfflineMode(mode: ExamMode): boolean {
  return OFFLINE_MODES.includes(mode);
}

// Псевдо-uuid без залежностей (crypto.randomUUID є у сучасних WebView/браузерах)
function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

type LocalSession = {
  id: string;
  mode: ExamMode;
  categoryId: number | null;
  total: number;
  startedAt: number;
  answers: Map<number, { answerId: number | null; isCorrect: boolean; timeMs: number }>;
};
// Активні офлайн-сесії в пам'яті (як і онлайн — питання живуть у стані сторінки,
// перезавантаження посеред тесту скидає його і онлайн, і офлайн).
const sessions = new Map<string, LocalSession>();
// Результати завершених офлайн-спроб — щоб повторний finish зі сторінки Result
// повернув ТЕ САМЕ (ідемпотентність, як серверний finish).
const finished = new Map<string, FinishAttemptResponse>();

async function ctx() {
  const licenseCategory = (await getMeta<string | null>('licenseCategory')) ?? null;
  const premiumUntil = await getMeta<string | null>('premiumUntil');
  const isPremium = !!premiumUntil && new Date(premiumUntil).getTime() > Date.now();
  return { licenseCategory, isPremium };
}

// Фільтр за категорією прав: порожній список = для всіх, інакше має містити категорію юзера
function licenceOk(q: OfflineQuestion, lc: string | null): boolean {
  if (!q.licenseCategories || q.licenseCategories.length === 0) return true;
  if (!lc) return true; // категорію юзера не знаємо — не ріжемо
  return q.licenseCategories.includes(lc);
}

// Прибираємо isCorrect з відповідей — щоб форма збігалася з серверним StartAttemptResponse
function strip(q: OfflineQuestion): Question {
  return {
    id: q.id,
    textUk: q.textUk,
    imageUrl: q.imageUrl,
    answers: q.answers
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((a) => ({ id: a.id, textUk: a.textUk, order: a.order })),
  };
}

export async function startOffline(mode: ExamMode, categoryId?: number): Promise<StartAttemptResponse> {
  if (!isOfflineMode(mode)) throw new Error('OFFLINE_MODE_UNSUPPORTED');
  const { licenseCategory, isPremium } = await ctx();
  // Безкоштовно офлайн — тільки OFFICIAL_20 (як онлайн)
  if (!isPremium && mode !== 'OFFICIAL_20') throw new Error('OFFLINE_PREMIUM_REQUIRED');

  const all = (await allQuestions()).filter((q) => licenceOk(q, licenseCategory));
  let pool: OfflineQuestion[];

  if (mode === 'CATEGORY') {
    if (!categoryId) throw new Error('OFFLINE_NO_CATEGORY');
    pool = all
      .filter((q) => q.categoryId === categoryId)
      .sort((a, b) => a.order - b.order || a.id - b.id);
  } else if (mode === 'HARDEST_100') {
    // Немає глобальної статистики офлайн — беремо найскладніші за полем difficulty
    pool = shuffle(all).sort((a, b) => b.difficulty - a.difficulty).slice(0, 100);
  } else {
    // OFFICIAL_20 / RANDOM_20 / MARATHON — випадкові
    let base = all;
    // Безкоштовний OFFICIAL_20 — той самий 40% ліміт, що на сервері (id % 5 < 2)
    if (mode === 'OFFICIAL_20' && !isPremium) base = base.filter((q) => q.id % 5 < 2);
    pool = shuffle(base);
  }

  const cfg = EXAM_CONFIG[mode];
  const count = cfg.questionCount === 'all' ? pool.length : Math.min(cfg.questionCount, pool.length);
  const questions = pool.slice(0, count);
  if (questions.length === 0) throw new Error('OFFLINE_NO_QUESTIONS');

  const id = LOCAL_PREFIX + uuid();
  sessions.set(id, {
    id,
    mode,
    categoryId: categoryId ?? null,
    total: questions.length,
    startedAt: Date.now(),
    answers: new Map(),
  });

  return {
    attemptId: id,
    mode,
    timeLimitSeconds: cfg.timeLimitSeconds,
    questions: questions.map(strip),
  };
}

export async function submitOffline(
  attemptId: string,
  input: { questionId: number; answerId: number | null; timeMs: number },
): Promise<SubmitAnswerResponse> {
  const s = sessions.get(attemptId);
  const q = await questionById(input.questionId);
  if (!q) throw new Error('OFFLINE_QUESTION_NOT_FOUND');
  const correct = q.answers.find((a) => a.isCorrect) ?? null;
  const chosen = input.answerId != null ? q.answers.find((a) => a.id === input.answerId) : undefined;
  const isCorrect = !!chosen && chosen.isCorrect;
  // OFFICIAL_20 дозволяє змінювати відповідь до фінішу (перезапис), інші — фіксуємо
  if (s && (s.mode === 'OFFICIAL_20' || !s.answers.has(input.questionId))) {
    s.answers.set(input.questionId, { answerId: input.answerId, isCorrect, timeMs: input.timeMs });
  }
  return {
    isCorrect,
    correctAnswerId: correct?.id ?? null,
    explanation: q.explanation,
    explanationCasual: q.explanationCasual ?? null,
  };
}

export async function finishOffline(attemptId: string): Promise<FinishAttemptResponse> {
  // Ідемпотентність: повторний виклик (напр. зі сторінки Result) → той самий результат.
  const already = finished.get(attemptId);
  if (already) return already;

  const s = sessions.get(attemptId);
  if (!s) {
    // Сесію втрачено (перезавантаження посеред тесту) — нейтральний результат
    return { score: 0, total: 0, passed: false, timeSpentMs: 0, xpEarned: 0, newAchievements: [] };
  }
  const finishedAt = Date.now();
  const answersArr = [...s.answers.entries()].map(([questionId, a]) => ({
    questionId,
    answerId: a.answerId,
    timeMs: a.timeMs,
  }));
  const correctCount = [...s.answers.values()].filter((a) => a.isCorrect).length;
  const total = s.total;
  const passThreshold = EXAM_CONFIG[s.mode].passThreshold;
  const passed = total > 0 && correctCount / total >= passThreshold;
  const xpEarned =
    correctCount * XP_REWARDS.PER_CORRECT_ANSWER +
    (passed ? XP_REWARDS.TEST_PASSED_BONUS : 0) +
    (correctCount === total ? XP_REWARDS.PERFECT_SCORE_BONUS : 0);
  const timeSpentMs = finishedAt - s.startedAt;

  const outbox: OutboxAttempt = {
    id: attemptId.slice(LOCAL_PREFIX.length), // на сервер шлемо чистий uuid як Attempt.id
    mode: s.mode,
    categoryId: s.categoryId,
    total,
    startedAt: new Date(s.startedAt).toISOString(),
    finishedAt: new Date(finishedAt).toISOString(),
    answers: answersArr,
    score: correctCount,
    passed,
    xpEarned,
    createdAt: finishedAt,
  };
  await enqueueAttempt(outbox);
  // Зберігаємо результат, щоб екран Result (який ще раз викличе finish) показав те саме
  finished.set(attemptId, {
    score: correctCount,
    total,
    passed,
    timeSpentMs,
    xpEarned,
    newAchievements: [],
  });
  sessions.delete(attemptId);
  return finished.get(attemptId)!;
}

export function finishedResult(attemptId: string): FinishAttemptResponse | undefined {
  return finished.get(attemptId);
}
